import React, { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Sparkles } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { formatCompactPriceRange } from "@/lib/carCounts";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { SITE_CONTAINER } from "@/lib/siteLayout";

const HERO_VIDEO_DARK_MP4 = "/hero/hero-car.mp4";
const HERO_VIDEO_DARK_MOV = "/hero/hero-car.mov";
const HERO_VIDEO_LIGHT_MP4 = "/hero/hero-car-light.mp4";

/** Szöveg megjelenése: autó középre ér */
const REVEAL_RATIO = 0.44;
/** Megállás: autó középen, kész — nincs kigurulás */
const PAUSE_RATIO = 0.52;

/**
 * @param {{ loading?: boolean, count?: number, priceRange?: { min: number, max: number } | null }} stats
 */
export const Hero = ({ stats }) => {
  const { content } = useSiteContent();
  const h = content.hero || DEFAULT_SITE_CONTENT.hero;
  const { theme } = useTheme();
  const isLight = theme === "light";
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const revealAtRef = useRef(5);
  const pauseAtRef = useRef(6.5);
  const durationRef = useRef(8);
  const scrubbingRef = useRef(false);
  /** intro (begurul) → hold (középen megáll, nem indul újra) */
  const rolloutPhaseRef = useRef("intro");
  const lastScrollYRef = useRef(0);
  const [showContent, setShowContent] = useState(false);

  /** Görgetés közben ne ugorjon középre — csak folytassa a begurulást, ha a böngésző megállította. */
  const resumeIntroRollIn = useCallback(() => {
    const video = videoRef.current;
    if (!video || rolloutPhaseRef.current !== "intro") return;
    if (video.currentTime >= pauseAtRef.current - 0.03) return;
    if (video.paused) {
      video.play().catch(() => {});
    }
  }, []);

  const videoMp4 = isLight
    ? h.video_mp4_light || HERO_VIDEO_LIGHT_MP4
    : h.video_mp4 || HERO_VIDEO_DARK_MP4;

  const resetForTheme = useCallback(() => {
    scrubbingRef.current = false;
    rolloutPhaseRef.current = "intro";
    lastScrollYRef.current = 0;
    setShowContent(false);
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  }, []);

  useEffect(() => {
    resetForTheme();
  }, [isLight, videoMp4, resetForTheme]);

  const handleLoadedMetadata = useCallback((e) => {
    const video = e.currentTarget;
    const duration = video.duration;
    if (!Number.isFinite(duration)) return;
    durationRef.current = duration;
    revealAtRef.current = duration * REVEAL_RATIO;
    pauseAtRef.current = duration * PAUSE_RATIO;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      rolloutPhaseRef.current = "hold";
      video.pause();
      video.currentTime = pauseAtRef.current;
      setShowContent(true);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const scrollingDown = window.scrollY > lastScrollYRef.current + 1;
    lastScrollYRef.current = window.scrollY;

    if (rolloutPhaseRef.current === "intro" && scrollingDown && window.scrollY > 4) {
      resumeIntroRollIn();
    }
  }, [resumeIntroRollIn]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  /** Háttérben ne pörögjön a hero videó — kevesebb memória (Chrome/Safari hosszú tab). */
  useEffect(() => {
    const onVis = () => {
      const video = videoRef.current;
      if (!video) return;
      if (document.hidden) {
        video.pause();
        return;
      }
      if (rolloutPhaseRef.current === "intro" || rolloutPhaseRef.current === "hold") {
        if (rolloutPhaseRef.current === "hold") return;
        video.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || scrubbingRef.current) return;

    if (video.currentTime >= revealAtRef.current) {
      setShowContent(true);
    }

    if (rolloutPhaseRef.current === "intro" && video.currentTime >= pauseAtRef.current) {
      video.pause();
      video.currentTime = pauseAtRef.current;
      rolloutPhaseRef.current = "hold";
    }
  }, []);

  const contentMotion = (delay = 0) =>
    showContent
      ? { opacity: 1, y: 0, transition: { duration: 0.8, delay } }
      : { opacity: 0, y: 30 };

  const statsLoading = !!stats?.loading;
  const carCount = statsLoading ? "…" : String(stats?.count ?? 0);
  const priceSpan = statsLoading
    ? "…"
    : stats?.priceRange
      ? formatCompactPriceRange(stats.priceRange.min, stats.priceRange.max)
      : "—";

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-testid="hero-section"
      className={`relative ${isLight ? "bg-[#F5F6FA]" : "bg-anthracite-900"}`}
    >
      <div className="sticky top-0 min-h-[100dvh] h-[100dvh] flex items-start md:items-center pt-28 md:pt-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden isolate">
          <video
            key={videoMp4}
            ref={videoRef}
            autoPlay
            muted
            playsInline
            preload={typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches ? "metadata" : "auto"}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            className="w-full h-full object-cover pointer-events-none [transform:translateZ(0)]"
            aria-hidden
          >
            <source src={videoMp4} type="video/mp4" />
            {!isLight && (h.video_mov || HERO_VIDEO_DARK_MOV) ? (
              <source src={h.video_mov || HERO_VIDEO_DARK_MOV} type="video/quicktime" />
            ) : null}
          </video>

          <div
            data-testid="hero-overlay-vertical"
            className={`absolute inset-0 transition-opacity duration-700 ${
              showContent ? "opacity-100" : isLight ? "opacity-0" : "opacity-40"
            } ${
              isLight
                ? "bg-gradient-to-t from-[#F5F6FA] via-[#F5F6FA]/70 to-[#F5F6FA]/30"
                : "bg-gradient-to-t from-anthracite-900 via-anthracite-900/70 to-anthracite-900/30"
            }`}
          />
          <div
            data-testid="hero-overlay-horizontal"
            className={`absolute inset-0 transition-opacity duration-700 ${
              showContent ? "opacity-100" : "opacity-0"
            } ${
              isLight
                ? "bg-gradient-to-r from-white/85 via-white/40 to-transparent"
                : "bg-gradient-to-r from-anthracite-900/90 to-transparent"
            }`}
          />
        </div>

        <div className={`relative z-10 w-full ${SITE_CONTAINER}`}>
          <motion.div
            animate={contentMotion(0)}
            initial={{ opacity: 0, y: 30 }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 ${
              isLight ? "bg-white/70 backdrop-blur-md border border-slate-200" : "glass"
            }`}
          >
            <Sparkles className="w-4 h-4 text-electric" strokeWidth={1.5} />
            <span className={`text-xs tracking-[0.2em] uppercase ${isLight ? "text-slate-700" : "text-slate-300"}`}>
              {h.badge}
            </span>
          </motion.div>

          <motion.h1
            animate={contentMotion(0.15)}
            initial={{ opacity: 0, y: 40 }}
            className={`font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter max-w-3xl leading-[1.05] ${
              isLight ? "text-slate-900" : "text-white"
            }`}
          >
            {h.title_before}
            <span className="text-electric">{h.title_highlight}</span>
            {h.title_after}
          </motion.h1>

          <motion.p
            animate={contentMotion(0.3)}
            initial={{ opacity: 0, y: 40 }}
            className={`mt-6 text-base md:text-lg max-w-xl leading-relaxed ${
              isLight ? "text-slate-700" : "text-slate-300"
            }`}
          >
            {h.subtitle}
          </motion.p>

          <motion.div
            animate={contentMotion(0.45)}
            initial={{ opacity: 0, y: 40 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={h.cta_primary_href || "#keszlet"}
              data-testid="hero-cta-inventory"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-electric hover:bg-electric-light text-white font-medium transition-all hover:blue-glow"
            >
              {h.cta_primary_label}
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={h.cta_secondary_href || "#ai"}
              data-testid="hero-cta-ai"
              className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium transition-all ${
                isLight
                  ? "bg-white/80 backdrop-blur-md border border-slate-200 hover:bg-white text-slate-900"
                  : "glass hover:bg-white/10 text-white"
              }`}
            >
              <Sparkles className="w-4 h-4 text-electric" strokeWidth={1.5} />
              {h.cta_secondary_label}
            </a>
          </motion.div>

          <motion.div
            animate={showContent ? { opacity: 1, transition: { duration: 1, delay: 0.6 } } : { opacity: 0 }}
            initial={{ opacity: 0 }}
            className="mt-16 flex flex-wrap gap-10"
          >
            {(h.stats || []).map((st) => {
              let value = st.value || "—";
              if (st.dynamic === "inventory_count") value = carCount;
              else if (st.dynamic === "price_range") value = priceSpan;
              else if (st.value_override) value = st.value_override;
              const testid =
                st.id === "count"
                  ? "hero-stat-count"
                  : st.id === "verified"
                    ? "hero-stat-verified"
                    : "hero-stat-price-range";
              return (
                <div key={st.id || st.label} data-testid={testid}>
                  <div className={`font-heading text-3xl font-light ${isLight ? "text-slate-900" : "text-white"}`}>
                    {value}
                  </div>
                  <div
                    className={`text-xs tracking-[0.15em] uppercase mt-1 ${isLight ? "text-slate-600" : "text-slate-400"}`}
                  >
                    {st.label}
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
